import React, { Dispatch, SetStateAction, useRef, useState } from "react";

import {
  HotelDetails,
  SearchParameters,
  wsAvailability,
} from "../../../assets/Services/wsSearch";
import { useTranslation } from "react-i18next";
import { currencySymbols } from "../../../assets/Utils/prices";
import Bowser from "bowser";
import { fireTagManagerEvent } from "../../../assets/Services/tagmanager";
import { ANALYTICS_SORT_BY } from "../../../config/analytics";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { useSelector } from "react-redux";
import { selectUser } from "../../../redux/slices/userSlice";
import IconTelephone from "../../../assets/icons/IconTelephone";
import Button from "../../../assets/TakyonDesignSystem/components/Button/Button";
import CollectionController from "../../../assets/Controllers/CollectionController";
import { useSearch } from "../../hooks/useSearch";
import DatesInput from "../Directsearch/DatesInput";
import GuestsInput from "../Directsearch/GuestsInput";
import IconBadgePlus from "../../../assets/icons/IconBadgePlus";
import { getQueryString } from "../../../assets/Utils/search";
import { useLocation } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import IconNew from "../../../assets/TakyonDesignSystem/components/Icon/Icon";
import { closeModal, openModal } from "../../../assets/Utils/modal";
import imageBadgePhoneNumber from "../../../assets/img/directsearch/badge_phone_number.avif";
import loadingGif from "../../../assets/img/icons/search.gif";
import { useCopyToClipboard } from "../../hooks/hooks";
import IconCopy from "../../../assets/icons/IconCopy";
import { toast } from "react-toastify";
import { getDisplayPrice, getOTADeltaPrice } from "../Directsearch/utils";

type Props = {
  isLoading: boolean;
  hotelDetail: HotelDetails;
  availability?: wsAvailability;
  searchParameters?: SearchParameters;
  isSmallScreen: boolean;
  isExtraDiscountApplied: boolean;
  setSearchParameters: (params?: SearchParameters) => void;
};

const AvailabilityCard = (props: Props) => {
  const {
    isLoading,
    hotelDetail,
    availability,
    searchParameters,
    isSmallScreen,
    isExtraDiscountApplied,
    setSearchParameters,
  } = props;
  const { t } = useTranslation();
  const bowserParser = Bowser.getParser(navigator.userAgent);
  const user = useSelector(selectUser);
  const { buildRedirectURL } = useSearch();
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [isGuestsOpen, setIsGuestsOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const locationInputRef = useRef<HTMLDivElement>(null);
  const calendarInputRef = useRef<HTMLDivElement>(null);

  const { copyToClipboard } = useCopyToClipboard();

  const md = useMediaQuery({ query: "(max-width: 768px)" });

  const { pathname } = useLocation();

  const urlGoogle = `https://google.com/search?q=${encodeURIComponent(
    `${hotelDetail?.name.replace(/&/g, "")} ${
      searchParameters?.destinationLabel?.split(",")[0]
    }`,
  )}`;

  let urlWebsite = hotelDetail?.website;
  if (urlWebsite && !urlWebsite.includes("http"))
    urlWebsite = `https://${urlWebsite}`;

  const fallbackUrl = urlWebsite ?? urlGoogle;

  const isMobile = bowserParser.getPlatformType() === "mobile"


  if (!searchParameters) return null;

  return (
    <div
      className="w-full flex flex-col items-center overscroll-contain rounded-2xl shadow-md border border-[#E8E8E8]"
      style={{
        background: isExtraDiscountApplied
          ? "linear-gradient(0deg,rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 100%)"
          : "transparent",
      }}
    >
      <div className="w-full flex flex-col justify-between items-center gap-2 px-4 py-3 bg-white rounded-2xl">
        <div className="flex items-center gap-1 shrink-0">
          {hotelDetail.isActive && <IconBadgePlus className="size-4" />}
          <span className="text-black text-sm font-medium">
            {t("ds.official_website")}
          </span>
        </div>
        {isLoading ? (
          <div>
            <img src={loadingGif} alt="loading" className="w-4 h-4" />
          </div>
        ) : availability ? (
          <div className="flex flex-col items-center justify-center gap-1">
            {availability.availabilityInfo.available ? (
              <div className="flex justify-between items-end gap-0.5">
                <p className="text-xs font-medium">{t("ds.from2")}</p>
                <p className="font-medium text-2xl translate-y-1">
                  {getDisplayPrice({
                    availability,
                    deltaBooking: hotelDetail.deltaPriceBookingCom,
                    deltaHotels: hotelDetail.deltaPriceHotelsCom,
                    lang: searchParameters.language,
                  })}
                </p>
              </div>
            ) : (
              <div className="flex rounded-lg px-2 py-1 justify-center items-center bg-[#E8E8E8]">
                <p className="text-xs font-medium text-[#747474]">
                  {t(
                    isSmallScreen
                      ? "ds.no_availability_short"
                      : "ds.no_availability",
                  )}
                </p>
              </div>
            )}

            {availability.availabilityInfo.available ? (
              <div className="flex items-center justify-center">
                <p className="text-xs text-[#EF4444] font-medium text-center">
                  {getOTADeltaPrice({
                    availability,
                    deltaBooking: hotelDetail.deltaPriceBookingCom,
                    deltaHotels: hotelDetail.deltaPriceHotelsCom,
                    lang: searchParameters.language,
                  })}
                </p>
              </div>
            ) : null}
          </div>
        ) : null}
        {!md ? (
          <div className="flex-col w-full flex gap-1">
            <DatesInput
              mode="desktop"
              isSmallScreen={isSmallScreen}
              isCalendarOpen={isCalendarOpen}
              setIsCalendarOpen={setIsCalendarOpen}
              setIsLocationOpen={setIsLocationOpen}
              setIsGuestsOpen={setIsGuestsOpen}
              calendarInputRef={calendarInputRef}
              setParameters={setSearchParameters}
              params={searchParameters}
              parent="detail"
            />
            <GuestsInput
              mode="desktop"
              isGuestsOpen={isGuestsOpen}
              setIsGuestsOpen={setIsGuestsOpen}
              setIsLocationOpen={setIsLocationOpen}
              setIsCalendarOpen={setIsCalendarOpen}
              calendarInputRef={calendarInputRef}
              locationInputRef={locationInputRef}
              parent="detail"
            />
          </div>
        ) : (
          <div className="flex-col w-full flex md:hidden gap-1">
            <DatesInput
              mode="mobile"
              isSmallScreen={isSmallScreen}
              isCalendarOpen={isCalendarOpen}
              setIsCalendarOpen={setIsCalendarOpen}
              setIsLocationOpen={setIsLocationOpen}
              setIsGuestsOpen={setIsGuestsOpen}
              calendarInputRef={calendarInputRef}
              setParameters={setSearchParameters}
              params={searchParameters}
              parent="detail"
            />
            <GuestsInput
              mode="mobile"
              isGuestsOpen={isGuestsOpen}
              setIsGuestsOpen={setIsGuestsOpen}
              setIsLocationOpen={setIsLocationOpen}
              setIsCalendarOpen={setIsCalendarOpen}
              calendarInputRef={calendarInputRef}
              locationInputRef={locationInputRef}
              parent="detail"
            />
          </div>
        )}

        {hotelDetail.isActive ? (
          <div className="md:flex items-center gap-2 w-full hidden">
            <a
              href={`tel:${hotelDetail.phone}`}
              className="flex md:hidden items-center justify-center size-12 aspect-square bg-[#F3F3F3] rounded-full no-underline"
              onClick={(event) => {
                fireTagManagerEvent("call_hotel", {
                  destination: !!searchParameters?.destinationLabel
                    ? searchParameters.destinationLabel
                    : null,
                  is_price_loading: !availability,
                  amount: availability
                    ? Number(availability.availabilityInfo.value.toFixed(0))
                    : 0,
                  collection_id: hotelDetail._id,
                  collection_name: hotelDetail.name,
                  is_available:
                    availability && availability.availabilityInfo.available,
                  is_free: !hotelDetail.isActive,
                  is_loyalty_enabled:
                    !!hotelDetail.isLoyaltyEnabled &&
                    !!hotelDetail?.isLoyaltyEnabledAdmin,
                  list_position: null,
                  source: "Details",
                  user_id: user?._id ?? null,
                  stars:
                    hotelDetail.stars && Number(hotelDetail.stars) !== 0
                      ? Number(hotelDetail.stars)
                      : null,
                  tag: !!searchParameters?.tag ? searchParameters.tag : null,
                  adults: searchParameters?.adults ?? null,
                  children: searchParameters?.childrenAge.length,
                  sort_type: searchParameters?.sortBy
                    ? ANALYTICS_SORT_BY[
                        (
                          {
                            recommended: "RECOMMENDED",
                            distance_to_cc: "DISTANCE",
                            reviews_score: "REVIEWS",
                            price: "PRICE",
                          } as const
                        )[searchParameters.sortBy]
                      ]
                    : ANALYTICS_SORT_BY.RECOMMENDED,
                  categories_filter: JSON.stringify(searchParameters?.filters ?? {}),
                  check_in: searchParameters?.arrivalDate
                    ? format(
                        new Date(searchParameters.arrivalDate),
                        "dd-MM-yyyy",
                        {
                          locale: it,
                        },
                      )
                    : null,
                  check_out: searchParameters?.departureDate
                    ? format(
                        new Date(searchParameters.departureDate),
                        "dd-MM-yyyy",
                        {
                          locale: it,
                        },
                      )
                    : null,
                  includes_benefits: hotelDetail?.advantagesOnDirectWebsite?.length > 0,
                });

                 if (!isMobile) {
                            event.preventDefault();
                            openModal({
                              content: (
                                <div className="flex flex-col p-8 items-center justify-center gap-4 flex-1">
                                  <button
                                      type="button"
                                      onClick={closeModal}
                                      className="bg-transparent border-none touch-hitbox absolute top-4 right-4"
                                  >
                                    <IconNew icon="close" className="size-5" />
                                  </button>
                                  <img src={imageBadgePhoneNumber} role="presentation" className="size-24 shrink-0 mx-auto" />
                                  <div className="flex flex-col gap-1">
                                    <h3 className="font-medium text-[#747474] text-center mb-0 text-base">
                                      {t("ds.call_structure", {
                                        name: hotelDetail.name
                                      })}
                                    </h3>
                                    <p className="text-lg font-medium text-black text-center">{hotelDetail.phone}</p>
                                  </div>
                                  <div className="flex items-center">
                                    <button type="button" className="flex items-center gap-1 bg-transparent border-none text-[#A2A2A2] flex-1" onClick={() => {
                                      copyToClipboard(hotelDetail.phone ?? "");
                                      toast.success(t("ds.phone_number_copied"));
                                    }}>
                                      <IconCopy className="size-4" />
                                      <p className="text-xs font-medium">{t("ds.copy")}</p>
                                    </button>
                                  </div>
                                  {isExtraDiscountApplied && (
                                    <p className="text-sm font-medium text-center mt-8 text-[#747474] flex flex-wrap items-center gap-1 justify-center">
                                      <span>
                                        {t("ds.dont_forget_discount.0")}
                                      </span>
                                      <span className="text-black">
                                        {t("ds.dont_forget_discount.1", {
                                          discountCode: "TAKYON"
                                        })}
                                      </span>
                                      <span>
                                        {t("ds.dont_forget_discount.2")}
                                      </span>
                                      <span className="text-black">
                                        {t("ds.dont_forget_discount.3", {
                                          percentage: hotelDetail.loyatyDiscountPercentage
                                        })}
                                      </span>
                                    </p>
                                  )}
                                </div>
                              ),
                              className: "rounded-t-3xl h-fit self-end lg:h-auto lg:self-center lg:rounded-3xl lg:max-w-lg"
                            }, {
                              hideModalCloseBtn: true,
                            })
                          }
              }}
            >
              <IconTelephone className="size-5 text-black" />
            </a>

            <a
              href={`tel:${hotelDetail.phone}`}
              className="md:flex hidden items-center justify-center size-12 aspect-square bg-[#F3F3F3] rounded-full border-none"
              onClick={(event) => {
                fireTagManagerEvent("call_hotel", {
                  destination: !!searchParameters.destinationLabel
                    ? searchParameters.destinationLabel
                    : null,
                  amount: hotelDetail.availability
                    ? Number(
                        hotelDetail.availability.availabilityInfo.value.toFixed(
                          0,
                        ),
                      )
                    : 0,
                  collection_id: hotelDetail._id,
                  collection_name: hotelDetail.name,
                  is_available:
                    hotelDetail.availability &&
                    hotelDetail.availability.availabilityInfo.available,
                  is_free: !hotelDetail.isActive,
                  is_loyalty_enabled:
                    !!hotelDetail.isLoyaltyEnabled &&
                    !!hotelDetail?.isLoyaltyEnabledAdmin,
                  list_position: null,
                  source: "List",
                  user_id: user?._id ?? null,
                  stars:
                    hotelDetail.stars && Number(hotelDetail.stars) !== 0
                      ? Number(hotelDetail.stars)
                      : null,
                  tag: !!searchParameters.tag ? searchParameters.tag : null,
                  adults: searchParameters.adults ?? null,
                  children: searchParameters.childrenAge.length,
                  sort_type: searchParameters.sortBy
                    ? ANALYTICS_SORT_BY[
                        (
                          {
                            recommended: "RECOMMENDED",
                            distance_to_cc: "DISTANCE",
                            reviews_score: "REVIEWS",
                            price: "PRICE",
                          } as const
                        )[searchParameters.sortBy]
                      ]
                    : ANALYTICS_SORT_BY.RECOMMENDED,
                  categories_filter: JSON.stringify(searchParameters.filters ?? {}),
                  check_in: searchParameters.arrivalDate
                    ? format(
                        new Date(searchParameters.arrivalDate),
                        "dd-MM-yyyy",
                        {
                          locale: it,
                        },
                      )
                    : null,
                  check_out: searchParameters.departureDate
                    ? format(
                        new Date(searchParameters.departureDate),
                        "dd-MM-yyyy",
                        {
                          locale: it,
                        },
                      )
                    : null,
                  includes_benefits: hotelDetail?.advantagesOnDirectWebsite?.length > 0,
                });

                if (!isMobile) {
                            event.preventDefault();
                            openModal({
                              content: (
                                <div className="flex flex-col p-8 items-center justify-center gap-4 flex-1">
                                  <button
                                      type="button"
                                      onClick={closeModal}
                                      className="bg-transparent border-none touch-hitbox absolute top-4 right-4"
                                  >
                                    <IconNew icon="close" className="size-5" />
                                  </button>
                                  <img src={imageBadgePhoneNumber} role="presentation" className="size-24 shrink-0 mx-auto" />
                                  <div className="flex flex-col gap-1">
                                    <h3 className="font-medium text-[#747474] text-center mb-0 text-base">
                                      {t("ds.call_structure", {
                                        name: hotelDetail.name
                                      })}
                                    </h3>
                                    <p className="text-lg font-medium text-black text-center">{hotelDetail.phone}</p>
                                  </div>
                                  <div className="flex items-center">
                                    <button type="button" className="flex items-center gap-1 bg-transparent border-none text-[#A2A2A2] flex-1" onClick={() => {
                                      copyToClipboard(hotelDetail.phone ?? "");
                                      toast.success(t("ds.phone_number_copied"));
                                    }}>
                                      <IconCopy className="size-4" />
                                      <p className="text-xs font-medium">{t("ds.copy")}</p>
                                    </button>
                                  </div>
                                  {isExtraDiscountApplied && (
                                    <p className="text-sm font-medium text-center mt-8 text-[#747474] flex flex-wrap items-center gap-1 justify-center">
                                      <span>
                                        {t("ds.dont_forget_discount.0")}
                                      </span>
                                      <span className="text-black">
                                        {t("ds.dont_forget_discount.1", {
                                          discountCode: "TAKYON"
                                        })}
                                      </span>
                                      <span>
                                        {t("ds.dont_forget_discount.2")}
                                      </span>
                                      <span className="text-black">
                                        {t("ds.dont_forget_discount.3", {
                                          percentage: hotelDetail.loyatyDiscountPercentage
                                        })}
                                      </span>
                                    </p>
                                  )}
                                </div>
                              ),
                              className: "rounded-t-3xl h-fit self-end lg:h-auto lg:self-center lg:rounded-3xl lg:max-w-lg"
                            }, {
                              hideModalCloseBtn: true,
                            })
                          }
              }}
            >
              <IconTelephone className="text-black size-5" />
            </a>

            <Button
              size="sm"
              className="h-12 w-full"
              textClass="text-sm font-medium"
              text={t("ds.book_webiste")}
              onClick={() => {
                CollectionController.clickOnCollection(hotelDetail._id, {
                  btn: "visit_website",
                  directPrice: availability?.availabilityInfo.value,
                  cutPrice: undefined,
                  bookingPrice: undefined,
                  hotelsPrice: "N/A",
                  bookingComId: hotelDetail.bookingId,
                  checkin: searchParameters?.arrivalDate,
                  checkout: searchParameters?.departureDate,
                });
                const newTabUrl = buildRedirectURL(
                  hotelDetail._id,
                  fallbackUrl,
                );
                fireTagManagerEvent("book_on_site", {
                  destination: !!searchParameters?.destinationLabel
                    ? searchParameters.destinationLabel
                    : null,
                  is_price_loading: !availability,
                  amount: availability
                    ? Number(availability.availabilityInfo.value.toFixed(0))
                    : 0,
                  collection_id: hotelDetail._id,
                  collection_name: hotelDetail.name,
                  is_available:
                    availability && availability.availabilityInfo.available,
                  is_free: !hotelDetail.isActive,
                  is_loyalty_enabled:
                    !!hotelDetail.isLoyaltyEnabled &&
                    !!hotelDetail?.isLoyaltyEnabledAdmin,
                  list_position: null,
                  user_id: user?._id ?? null,
                  stars:
                    hotelDetail.stars && Number(hotelDetail.stars) !== 0
                      ? Number(hotelDetail.stars)
                      : null,
                  tag: !!searchParameters?.tag ? searchParameters.tag : null,
                  adults: searchParameters?.adults ?? null,
                  children: searchParameters?.childrenAge.length,
                  sort_type: searchParameters?.sortBy
                    ? ANALYTICS_SORT_BY[
                        (
                          {
                            recommended: "RECOMMENDED",
                            distance_to_cc: "DISTANCE",
                            reviews_score: "REVIEWS",
                            price: "PRICE",
                          } as const
                        )[searchParameters.sortBy]
                      ]
                    : ANALYTICS_SORT_BY.RECOMMENDED,
                  categories_filter: JSON.stringify(searchParameters?.filters ?? {}),
                  check_in: searchParameters?.arrivalDate
                    ? format(
                        new Date(searchParameters.arrivalDate),
                        "dd-MM-yyyy",
                        {
                          locale: it,
                        },
                      )
                    : null,
                  check_out: searchParameters?.departureDate
                    ? format(
                        new Date(searchParameters.departureDate),
                        "dd-MM-yyyy",
                        {
                          locale: it,
                        },
                      )
                    : null,
                  includes_benefits: hotelDetail?.advantagesOnDirectWebsite?.length > 0,
                });
                window.open(newTabUrl, "_blank");
              }}
              disabled={
                !availability || !availability.availabilityInfo.available
              }
            />
          </div>
        ) : (
          <div className="flex items-center w-full gap-1">
            <a
              href={`tel:${hotelDetail.phone}`}
              className="bg-[#F3F3F3] text-xs rounded-xl gap-1 p-4 w-1/2 flex items-center justify-center border-none text-black no-underline"
              onClick={(event) => {
                fireTagManagerEvent("call_hotel", {
                  destination: !!searchParameters.destinationLabel
                    ? searchParameters.destinationLabel
                    : null,
                  amount: availability
                    ? Number(availability.availabilityInfo.value.toFixed(0))
                    : 0,
                  collection_id: hotelDetail._id,
                  collection_name: hotelDetail.name,
                  is_available:
                    availability && availability.availabilityInfo.available,
                  is_free: !hotelDetail.isActive,
                  is_loyalty_enabled:
                    !!hotelDetail.isLoyaltyEnabled &&
                    !!hotelDetail?.isLoyaltyEnabledAdmin,
                  list_position: null,
                  source: "List",
                  user_id: user?._id ?? null,
                  stars:
                    hotelDetail.stars && Number(hotelDetail.stars) !== 0
                      ? Number(hotelDetail.stars)
                      : null,
                  tag: !!searchParameters.tag ? searchParameters.tag : null,
                  adults: searchParameters.adults ?? null,
                  children: searchParameters.childrenAge.length,
                  sort_type: searchParameters.sortBy
                    ? ANALYTICS_SORT_BY[
                        (
                          {
                            recommended: "RECOMMENDED",
                            distance_to_cc: "DISTANCE",
                            reviews_score: "REVIEWS",
                            price: "PRICE",
                          } as const
                        )[searchParameters.sortBy]
                      ]
                    : ANALYTICS_SORT_BY.RECOMMENDED,
                  categories_filter: JSON.stringify(searchParameters.filters ?? {}),
                  check_in: searchParameters.arrivalDate
                    ? format(
                        new Date(searchParameters.arrivalDate),
                        "dd-MM-yyyy",
                        {
                          locale: it,
                        },
                      )
                    : null,
                  check_out: searchParameters.departureDate
                    ? format(
                        new Date(searchParameters.departureDate),
                        "dd-MM-yyyy",
                        {
                          locale: it,
                        },
                      )
                    : null,
                  includes_benefits: hotelDetail?.advantagesOnDirectWebsite?.length > 0,
                });

                if (!isMobile) {
                            event.preventDefault();
                            openModal({
                              content: (
                                <div className="flex flex-col p-8 items-center justify-center gap-4 flex-1">
                                  <button
                                      type="button"
                                      onClick={closeModal}
                                      className="bg-transparent border-none touch-hitbox absolute top-4 right-4"
                                  >
                                    <IconNew icon="close" className="size-5" />
                                  </button>
                                  <img src={imageBadgePhoneNumber} role="presentation" className="size-24 shrink-0 mx-auto" />
                                  <div className="flex flex-col gap-1">
                                    <h3 className="font-medium text-[#747474] text-center mb-0 text-base">
                                      {t("ds.call_structure", {
                                        name: hotelDetail.name
                                      })}
                                    </h3>
                                    <p className="text-lg font-medium text-black text-center">{hotelDetail.phone}</p>
                                  </div>
                                  <div className="flex items-center">
                                    <button type="button" className="flex items-center gap-1 bg-transparent border-none text-[#A2A2A2] flex-1" onClick={() => {
                                      copyToClipboard(hotelDetail.phone ?? "");
                                      toast.success(t("ds.phone_number_copied"));
                                    }}>
                                      <IconCopy className="size-4" />
                                      <p className="text-xs font-medium">{t("ds.copy")}</p>
                                    </button>
                                  </div>
                                  {isExtraDiscountApplied && (
                                    <p className="text-sm font-medium text-center mt-8 text-[#747474] flex flex-wrap items-center gap-1 justify-center">
                                      <span>
                                        {t("ds.dont_forget_discount.0")}
                                      </span>
                                      <span className="text-black">
                                        {t("ds.dont_forget_discount.1", {
                                          discountCode: "TAKYON"
                                        })}
                                      </span>
                                      <span>
                                        {t("ds.dont_forget_discount.2")}
                                      </span>
                                      <span className="text-black">
                                        {t("ds.dont_forget_discount.3", {
                                          percentage: hotelDetail.loyatyDiscountPercentage
                                        })}
                                      </span>
                                    </p>
                                  )}
                                </div>
                              ),
                              className: "rounded-t-3xl h-fit self-end lg:h-auto lg:self-center lg:rounded-3xl lg:max-w-lg"
                            }, {
                              hideModalCloseBtn: true,
                            })
                          }
              }}
            >
              <IconNew icon="phone" type="symbol" isLucid size={18} />
              {t("ds.call")}
              {/* {t("ds.read_more")} */}
            </a>
            <a
              type="button"
              className="bg-[#F3F3F3] text-xs rounded-xl gap-1 p-4 w-1/2 flex items-center justify-center border-none text-black"
              href={hotelDetail.website}
              target="_blank"
              rel="noreferrer"
              style={
                !hotelDetail.website
                  ? { opacity: 0.5, cursor: "not-allowed" }
                  : {}
              }
              onClick={() => {
                fireTagManagerEvent("visit_hotel_site", {
                  destination: !!searchParameters.destinationLabel
                    ? searchParameters.destinationLabel
                    : null,
                  amount: hotelDetail.availability
                                      ? Number(
                                          hotelDetail.availability.availabilityInfo.value.toFixed(
                                            0,
                                          ),
                                        )
                                      : 0,
                  collection_id: hotelDetail._id, 
                  collection_name: hotelDetail.name, 
                  is_available: hotelDetail.availability && hotelDetail.availability.availabilityInfo.available, 
                  is_free: !hotelDetail.isActive, 
                  is_loyalty_enabled: !!hotelDetail.isLoyaltyEnabled && !!hotelDetail?.isLoyaltyEnabledAdmin,
                  list_position: null, 
                  source: "Details", 
                  user_id: user?._id ?? null,
                  stars: hotelDetail.stars && Number(hotelDetail.stars) !== 0
                                        ? Number(hotelDetail.stars)
                                        : null,
                  tag: !!searchParameters?.tag ? searchParameters.tag : null, 
                  adults: searchParameters?.adults ?? null, 
                  children: searchParameters?.childrenAge.length, 
                  sort_type: searchParameters?.sortBy
                                      ? ANALYTICS_SORT_BY[
                                          (
                                            {
                                              recommended: "RECOMMENDED",
                                              distance_to_cc: "DISTANCE",
                                              reviews_score: "REVIEWS",
                                              price: "PRICE",
                                            } as const
                                          )[searchParameters.sortBy]
                                        ]
                                      : ANALYTICS_SORT_BY.RECOMMENDED, 
                  categories_filter: JSON.stringify(searchParameters?.filters ?? {}), 
                  check_in: searchParameters?.arrivalDate
                                      ? format(
                                          new Date(searchParameters.arrivalDate),
                                          "dd-MM-yyyy",
                                          {
                                            locale: it,
                                          },
                                        )
                                      : null, 
                  check_out: searchParameters?.departureDate
                                      ? format(
                                          new Date(searchParameters.departureDate),
                                          "dd-MM-yyyy",
                                          {
                                            locale: it,
                                          },
                                        )
                                      : null, 
                  includes_benefit: hotelDetail?.advantagesOnDirectWebsite?.length > 0,
                })
              }} 
            >
              <IconNew icon="globe" type="symbol" isLucid size={18} />{" "}
              {t("ds.website")}
            </a>
          </div>
        )}
      </div>

      {isExtraDiscountApplied && hotelDetail.isActive && (
        <div className="w-full bg-black rounded-b-2xl hidden md:flex items-center justify-center px-4 h-6">
          <p className="text-white text-xs font-medium">
            {t("ds.loyalty_discount_with_code", {
              percentage: hotelDetail?.loyatyDiscountPercentage,
            })}
          </p>
        </div>
      )}
    </div>
  );
};

export default AvailabilityCard;
